import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { supabase } from '../supabase';
import useBreakpoint from '../hooks/useBreakpoint';
import { toastMobile, tap, pageMobile, dialog } from '../mobileStyles';

/*
  Configuración — admin-only view for managing team members' roles & access.
  Persists role + active toggle on the `equipo` row (role column optional).
  Clerk role lives on publicMetadata.role; for non-self users the admin UI here
  updates the Supabase mirror. A note explains the Clerk side is managed in Clerk.
*/

const WINE = '#6B1C2A';
const SUPER_ADMIN_EMAIL = 'jose.miguel.coa.2001@gmail.com';

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6h12" />
      <path d="M8 6V4a1 1 0 011-1h2a1 1 0 011 1v2" />
      <path d="M6 6l1 10a2 2 0 002 2h2a2 2 0 002-2l1-10" />
      <line x1="9" y1="9" x2="9" y2="15" />
      <line x1="11" y1="9" x2="11" y2="15" />
    </svg>
  );
}

export default function Configuracion() {
  const { user } = useUser();
  const { isMobile, isCompact } = useBreakpoint();
  const currentEmail = user?.primaryEmailAddress?.emailAddress || '';

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [hasRoleColumn, setHasRoleColumn] = useState(true);

  // Whitelist state
  const [whitelist, setWhitelist] = useState([]);
  const [wlLoading, setWlLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // whitelist row to delete

  function showToast(message, type = 'success') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('equipo')
      .select('*')
      .order('nombre', { ascending: true });
    if (!error && data) setMembers(data);
    setLoading(false);
  }, []);

  const fetchWhitelist = useCallback(async () => {
    setWlLoading(true);
    const { data, error } = await supabase
      .from('whitelist')
      .select('*')
      .order('nombre', { ascending: true });
    if (!error && data) setWhitelist(data);
    setWlLoading(false);
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);
  useEffect(() => { fetchWhitelist(); }, [fetchWhitelist]);

  async function updateMember(id, patch) {
    setSavingId(id);
    const { error } = await supabase.from('equipo').update(patch).eq('id', id);
    setSavingId(null);
    if (error) {
      // If column doesn't exist, keep going but warn once.
      if (/column .* does not exist/i.test(error.message)) {
        setHasRoleColumn(false);
        showToast('Columnas role/access_enabled no existen en la tabla equipo — añade la migración.', 'error');
        return;
      }
      showToast('Error: ' + error.message, 'error');
      return;
    }
    setMembers(prev => prev.map(m => m.id === id ? { ...m, ...patch } : m));
    showToast('Cambios guardados');
  }

  function deleteDisabledReason(row) {
    if (!row?.email) return null;
    if (row.email === currentEmail) return 'No puedes eliminarte a ti mismo';
    // Only the super admin can delete the super admin (and rule #1 above stops him
    // from deleting himself) — effectively the super admin row is undeletable.
    if (row.email === SUPER_ADMIN_EMAIL) return 'No se puede eliminar al super admin';
    return null;
  }

  async function confirmDelete() {
    const row = confirmTarget;
    if (!row) return;
    setDeletingId(row.id);
    const { data, error } = await supabase
      .from('whitelist')
      .delete()
      .eq('id', row.id)
      .select();
    setDeletingId(null);
    setConfirmTarget(null);
    if (error) {
      showToast('Error: ' + error.message, 'error');
      return;
    }
    if (!data || data.length === 0) {
      showToast('No se eliminó (posible bloqueo de RLS). Revisa políticas de la tabla whitelist.', 'error');
      return;
    }
    setWhitelist(prev => prev.filter(w => w.id !== row.id));
    showToast('Acceso eliminado correctamente');
  }

  return (
    <div style={isMobile ? { ...s.container, ...pageMobile } : s.container}>
      <header style={isMobile ? { ...s.header, ...s.headerMobile } : s.header}>
        <div>
          <h2 style={s.title}>Configuración</h2>
          <p style={isMobile ? { ...s.subtitle, ...s.textMobile } : s.subtitle}>Gestiona roles y accesos del equipo</p>
        </div>
        <div style={s.adminBadge}>Admin</div>
      </header>

      {!hasRoleColumn && (
        <div style={isMobile ? { ...s.warning, ...s.textMobile, overflowWrap: 'anywhere' } : s.warning}>
          <strong>Acción requerida en Supabase:</strong> la tabla <code>equipo</code> necesita columnas <code>role</code> (text) y <code>access_enabled</code> (boolean). Ejecuta la migración <code>supabase-configuracion-migration.sql</code>.
        </div>
      )}

      {/* WHITELIST — system access */}
      <section style={s.card}>
        <div style={isMobile ? { ...s.cardHeader, ...s.cardHeaderMobile } : s.cardHeader}>
          <div>
            <h3 style={s.cardTitle}>Accesos al sistema</h3>
            <p style={isMobile ? { ...s.cardSub, ...s.textMobile } : s.cardSub}>Usuarios autorizados para iniciar sesión</p>
          </div>
          <span style={s.count}>{whitelist.length} {whitelist.length === 1 ? 'acceso' : 'accesos'}</span>
        </div>

        {wlLoading ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>Cargando accesos…</div>
        ) : whitelist.length === 0 ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>No hay usuarios en el whitelist.</div>
        ) : isCompact ? (
          <div style={s.mList}>
            {whitelist.map(w => {
              const isSelf = w.email === currentEmail;
              const disabledReason = deleteDisabledReason(w);
              const disabled = !!disabledReason || deletingId === w.id;
              const roleLabel = w.rol === 'admin' ? 'Administrador' : w.rol === 'member' ? 'Miembro' : (w.rol || '—');
              const active = w.activo !== false;
              return (
                <div key={w.id} style={s.mCard}>
                  <div style={s.nameCell}>
                    <div style={{ ...s.avatar, background: `${WINE}22`, color: WINE, flexShrink: 0 }}>
                      {(w.nombre || w.email || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ ...s.name, ...s.mName }}>{w.nombre || '—'}{isSelf && <span style={s.selfTag}>tú</span>}</div>
                      <div style={s.mEmail}>{w.email}</div>
                    </div>
                  </div>
                  <div style={s.mRow}>
                    <span style={s.mLabel}>Rol</span>
                    <span style={{ ...s.rolePill, ...(w.rol === 'admin' ? s.rolePillAdmin : s.rolePillMember) }}>{roleLabel}</span>
                  </div>
                  <div style={s.mRow}>
                    <span style={s.mLabel}>Estado</span>
                    <span>
                      <span style={{ ...s.statusDot, background: active ? '#4A7C59' : '#B83B3B' }} />
                      <span style={{ fontSize: 15, color: active ? '#4A7C59' : '#B83B3B', fontWeight: 600 }}>
                        {active ? 'Activo' : 'Inactivo'}
                      </span>
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirmTarget(w)}
                    disabled={disabled}
                    aria-label={`Eliminar acceso de ${w.nombre || w.email}`}
                    style={{
                      ...s.deleteIconBtn,
                      ...(disabled ? s.deleteIconBtnDisabled : {}),
                      ...s.mFullBtn,
                    }}
                  >
                    <TrashIcon />
                    <span>Eliminar</span>
                  </button>
                  {disabledReason && <div style={s.mHint}>{disabledReason}</div>}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Nombre</th>
                  <th style={s.th}>Email</th>
                  <th style={s.th}>Rol</th>
                  <th style={s.th}>Estado</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {whitelist.map(w => {
                  const isSelf = w.email === currentEmail;
                  const disabledReason = deleteDisabledReason(w);
                  const disabled = !!disabledReason || deletingId === w.id;
                  const roleLabel = w.rol === 'admin' ? 'Administrador' : w.rol === 'member' ? 'Miembro' : (w.rol || '—');
                  const active = w.activo !== false;
                  return (
                    <tr key={w.id} style={s.tr}>
                      <td style={s.td}>
                        <div style={s.nameCell}>
                          <div style={{ ...s.avatar, background: `${WINE}22`, color: WINE }}>
                            {(w.nombre || w.email || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                          </div>
                          <div>
                            <div style={s.name}>{w.nombre || '—'}{isSelf && <span style={s.selfTag}>tú</span>}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ ...s.td, color: 'var(--text-secondary)' }}>{w.email}</td>
                      <td style={s.td}>
                        <span style={{ ...s.rolePill, ...(w.rol === 'admin' ? s.rolePillAdmin : s.rolePillMember) }}>
                          {roleLabel}
                        </span>
                      </td>
                      <td style={s.td}>
                        <span style={{ ...s.statusDot, background: active ? '#4A7C59' : '#B83B3B' }} />
                        <span style={{ fontSize: 12, color: active ? '#4A7C59' : '#B83B3B', fontWeight: 600 }}>
                          {active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td style={{ ...s.td, textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={() => setConfirmTarget(w)}
                          disabled={disabled}
                          title={disabledReason || 'Eliminar acceso'}
                          aria-label={`Eliminar acceso de ${w.nombre || w.email}`}
                          style={{
                            ...s.deleteIconBtn,
                            ...(disabled ? s.deleteIconBtnDisabled : {}),
                          }}
                        >
                          <TrashIcon />
                          <span>Eliminar</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section style={{ ...s.card, marginTop: 20 }}>
        <div style={isMobile ? { ...s.cardHeader, ...s.cardHeaderMobile } : s.cardHeader}>
          <h3 style={s.cardTitle}>Miembros del equipo</h3>
          <span style={s.count}>{members.length} personas</span>
        </div>

        {loading ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>Cargando miembros…</div>
        ) : members.length === 0 ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>No hay miembros. Añade gente desde la vista Equipo.</div>
        ) : isCompact ? (
          <div style={s.mList}>
            {members.map(m => {
              const isSelf = m.email && user?.primaryEmailAddress?.emailAddress === m.email;
              const role = m.role || 'member';
              const access = m.access_enabled !== false;
              return (
                <div key={m.id} style={s.mCard}>
                  <div style={s.nameCell}>
                    <div style={{ ...s.avatar, background: (m.color || WINE) + '22', color: m.color || WINE, flexShrink: 0 }}>
                      {(m.nombre || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ ...s.name, ...s.mName }}>{m.nombre}{isSelf && <span style={s.selfTag}>tú</span>}</div>
                      <div style={s.nameSub}>{m.estado || 'Activo'}</div>
                    </div>
                  </div>
                  <div style={s.mRow}><span style={s.mLabel}>Cargo</span><span style={s.mValue}>{m.cargo || '—'}</span></div>
                  <div style={s.mRow}><span style={s.mLabel}>Email</span><span style={s.mValue}>{m.email || '—'}</span></div>
                  <div style={s.mRow}>
                    <span style={s.mLabel}>Rol</span>
                    <select
                      value={role}
                      onChange={e => updateMember(m.id, { role: e.target.value })}
                      disabled={savingId === m.id}
                      style={{ ...s.select, ...(role === 'admin' ? s.selectAdmin : {}), ...s.mControl }}
                    >
                      <option value="member">Miembro</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>
                  <div style={s.mRow}>
                    <span style={s.mLabel}>Acceso</span>
                    <button
                      type="button"
                      onClick={() => updateMember(m.id, { access_enabled: !access })}
                      disabled={savingId === m.id}
                      style={{ ...s.toggle, ...(access ? s.toggleOn : s.toggleOff), ...s.mControl }}
                      aria-pressed={access}
                    >
                      <span style={{ ...s.toggleDot, ...(access ? s.toggleDotOn : {}) }} />
                      <span style={s.toggleLabel}>{access ? 'Activo' : 'Bloqueado'}</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Nombre</th>
                  <th style={s.th}>Cargo</th>
                  <th style={s.th}>Email</th>
                  <th style={s.th}>Rol</th>
                  <th style={s.th}>Acceso</th>
                </tr>
              </thead>
              <tbody>
                {members.map(m => {
                  const isSelf = m.email && user?.primaryEmailAddress?.emailAddress === m.email;
                  const role = m.role || 'member';
                  const access = m.access_enabled !== false;
                  return (
                    <tr key={m.id} style={s.tr}>
                      <td style={s.td}>
                        <div style={s.nameCell}>
                          <div style={{ ...s.avatar, background: (m.color || WINE) + '22', color: m.color || WINE }}>
                            {(m.nombre || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                          </div>
                          <div>
                            <div style={s.name}>{m.nombre}{isSelf && <span style={s.selfTag}>tú</span>}</div>
                            <div style={s.nameSub}>{m.estado || 'Activo'}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ ...s.td, color: 'var(--text-secondary)' }}>{m.cargo || '—'}</td>
                      <td style={{ ...s.td, color: 'var(--text-secondary)' }}>{m.email || '—'}</td>
                      <td style={s.td}>
                        <select
                          value={role}
                          onChange={e => updateMember(m.id, { role: e.target.value })}
                          disabled={savingId === m.id}
                          style={{ ...s.select, ...(role === 'admin' ? s.selectAdmin : {}) }}
                        >
                          <option value="member">Miembro</option>
                          <option value="admin">Administrador</option>
                        </select>
                      </td>
                      <td style={s.td}>
                        <button
                          type="button"
                          onClick={() => updateMember(m.id, { access_enabled: !access })}
                          disabled={savingId === m.id}
                          style={{ ...s.toggle, ...(access ? s.toggleOn : s.toggleOff) }}
                          aria-pressed={access}
                          title={access ? 'Acceso activo — clic para revocar' : 'Acceso revocado — clic para activar'}
                        >
                          <span style={{ ...s.toggleDot, ...(access ? s.toggleDotOn : {}) }} />
                          <span style={s.toggleLabel}>{access ? 'Activo' : 'Bloqueado'}</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section style={{ ...s.card, marginTop: 20 }}>
        <div style={isMobile ? { ...s.cardHeader, ...s.cardHeaderMobile } : s.cardHeader}>
          <h3 style={s.cardTitle}>Sobre los roles</h3>
        </div>
        <div style={isMobile ? s.roleGridMobile : { padding: '0 24px 22px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={s.roleCard}>
            <div style={{ ...s.roleTag, background: WINE }}>Administrador</div>
            <p style={isMobile ? { ...s.roleText, ...s.textMobile } : s.roleText}>Acceso total. Puede ver esta pantalla de Configuración, gestionar miembros y cambiar roles.</p>
          </div>
          <div style={s.roleCard}>
            <div style={{ ...s.roleTag, background: '#6B6B6B' }}>Miembro</div>
            <p style={isMobile ? { ...s.roleText, ...s.textMobile } : s.roleText}>Acceso a Dashboard, Gantt, Equipo, Mi Journal y WhatsApp. No ve Configuración.</p>
          </div>
        </div>
      </section>

      <p style={isMobile ? { ...s.footnote, ...s.footnoteMobile } : s.footnote}>
        El rol efectivo lo define Clerk (<code>publicMetadata.role</code>). Esta tabla es el espejo del equipo en la base de datos del estudio.
      </p>

      {/* Confirmation modal */}
      {confirmTarget && (
        <div style={isMobile ? { ...s.overlay, ...dialog.overlay } : s.overlay} onClick={() => !deletingId && setConfirmTarget(null)}>
          <div style={isMobile ? { ...s.confirmModal, ...dialog.panel, padding: 20 } : s.confirmModal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <div style={s.confirmIconWrap}>
              <TrashIcon />
            </div>
            <h3 style={s.confirmTitle}>Eliminar acceso</h3>
            <div style={s.confirmTargetCard}>
              <div style={s.confirmTargetName}>{confirmTarget.nombre || 'Usuario sin nombre'}</div>
              {confirmTarget.email && (
                <div style={s.confirmTargetEmail}>{confirmTarget.email}</div>
              )}
            </div>
            <p style={s.confirmBody}>
              ¿Estás seguro de que quieres eliminar el acceso de este usuario? Esta acción no se puede deshacer.
            </p>
            <div style={isMobile ? { ...s.confirmActions, flexWrap: 'wrap' } : s.confirmActions}>
              <button
                type="button"
                style={isMobile ? { ...s.cancelBtn, ...tap, flex: 1 } : s.cancelBtn}
                onClick={() => setConfirmTarget(null)}
                disabled={!!deletingId}
              >
                Cancelar
              </button>
              <button
                type="button"
                style={isMobile ? { ...s.dangerBtn, ...tap, flex: 1 } : s.dangerBtn}
                onClick={confirmDelete}
                disabled={!!deletingId}
              >
                {deletingId ? 'Eliminando…' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ ...s.toast, ...(toast.type === 'error' ? s.toastError : {}), ...(isMobile ? toastMobile : {}) }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

const s = {
  container: { padding: '32px 36px', maxWidth: 1200 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 600, color: 'var(--wine)', margin: 0 },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 },
  adminBadge: {
    padding: '6px 14px', borderRadius: 20, background: WINE, color: '#fff',
    fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
  },

  warning: {
    background: '#FFF7E8', border: '1px solid #E8C97B', color: '#8B6914',
    padding: '12px 16px', borderRadius: 10, fontSize: 13, marginBottom: 16,
  },

  card: { background: '#fff', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 14px' },
  cardTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600, color: 'var(--wine)', margin: 0 },
  cardSub: { fontSize: 12, color: 'var(--text-light)', marginTop: 2 },
  count: { fontSize: 11, fontWeight: 600, color: 'var(--wine)', background: 'rgba(107,28,42,0.06)', padding: '4px 12px', borderRadius: 20 },

  empty: { padding: '40px 24px', textAlign: 'center', color: 'var(--text-light)', fontSize: 14 },

  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '10px 24px', fontSize: 11, fontWeight: 600,
    color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.08em',
    borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
  },
  tr: { borderBottom: '1px solid #f3efe9' },
  td: { padding: '12px 24px', fontSize: 13, verticalAlign: 'middle' },

  nameCell: { display: 'flex', alignItems: 'center', gap: 12 },
  avatar: {
    width: 36, height: 36, borderRadius: '50%', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
  },
  name: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 },
  nameSub: { fontSize: 11, color: 'var(--text-light)' },
  selfTag: {
    fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
    background: 'rgba(107,28,42,0.1)', color: WINE, textTransform: 'uppercase', letterSpacing: '0.08em',
  },

  rolePill: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 20,
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
  },
  rolePillAdmin: { background: WINE, color: '#fff' },
  rolePillMember: { background: '#EEE8DD', color: '#6B6B6B' },

  statusDot: { display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 8, verticalAlign: 'middle' },

  select: {
    padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)',
    background: '#fff', fontSize: 12, fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600,
  },
  selectAdmin: { borderColor: WINE, color: WINE, background: 'rgba(107,28,42,0.04)' },

  toggle: {
    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px',
    borderRadius: 20, border: '1px solid transparent', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
    transition: 'all 0.2s ease',
  },
  toggleOn: { background: 'rgba(74,124,89,0.1)', color: '#4A7C59', borderColor: 'rgba(74,124,89,0.25)' },
  toggleOff: { background: 'rgba(184,59,59,0.08)', color: '#B83B3B', borderColor: 'rgba(184,59,59,0.25)' },
  toggleDot: {
    width: 8, height: 8, borderRadius: '50%', background: '#B83B3B',
  },
  toggleDotOn: { background: '#4A7C59' },
  toggleLabel: {},

  deleteIconBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px',
    borderRadius: 8, border: '1px solid rgba(184,59,59,0.3)', background: 'rgba(184,59,59,0.06)',
    color: '#B83B3B', cursor: 'pointer', fontSize: 12, fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
  },
  deleteIconBtnDisabled: {
    opacity: 0.4, cursor: 'not-allowed', background: 'transparent', borderColor: 'var(--border)', color: 'var(--text-light)',
  },

  roleCard: { padding: 14, borderRadius: 10, background: '#FDFBF8', border: '1px solid var(--border)' },
  roleTag: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 20,
    color: '#fff', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
  },
  roleText: { fontSize: 12, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.5 },

  footnote: { fontSize: 11, color: 'var(--text-light)', marginTop: 16 },

  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1500, backdropFilter: 'blur(2px)',
  },
  confirmModal: {
    background: '#fff', borderRadius: 16, width: '100%', maxWidth: 420,
    padding: '28px 28px 22px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', textAlign: 'center',
  },
  confirmIconWrap: {
    width: 48, height: 48, borderRadius: '50%', background: 'rgba(184,59,59,0.1)',
    color: '#B83B3B', display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 12px',
  },
  confirmTitle: {
    fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600,
    color: 'var(--wine)', margin: '4px 0 8px',
  },
  confirmTargetCard: {
    margin: '4px 0 14px', padding: '10px 14px', borderRadius: 10,
    background: '#FDFBF8', border: '1px solid var(--border)',
  },
  confirmTargetName: {
    fontSize: 15, fontWeight: 700, color: 'var(--text-primary)',
    fontFamily: "'DM Sans', sans-serif",
  },
  confirmTargetEmail: {
    fontSize: 12, color: 'var(--text-light)', marginTop: 2,
    fontFamily: 'monospace', wordBreak: 'break-all',
  },
  confirmBody: { fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 20px', lineHeight: 1.5 },
  confirmActions: { display: 'flex', gap: 10, justifyContent: 'center' },
  cancelBtn: {
    padding: '9px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent',
    fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", color: 'var(--text-secondary)',
  },
  dangerBtn: {
    padding: '9px 20px', borderRadius: 8, border: 'none', background: '#B83B3B', color: '#fff',
    fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },

  toast: {
    position: 'fixed', bottom: 24, right: 24, background: '#4A7C59', color: '#fff',
    padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 2000,
  },
  toastError: { background: '#B83B3B' },

  // Mobile-only additions (merged when useBreakpoint().isMobile)

  // Phase 4 mobile layout
  textMobile: { fontSize: 15 },
  headerMobile: { flexWrap: 'wrap', gap: 12 },
  cardHeaderMobile: { padding: '16px 16px 12px', flexWrap: 'wrap', gap: 8 },
  roleGridMobile: { padding: '0 16px 16px', display: 'grid', gridTemplateColumns: '1fr', gap: 12 },
  footnoteMobile: { fontSize: 13, overflowWrap: 'anywhere' },
  // Tables → stacked cards (mobile + tablet)
  mList: { display: 'flex', flexDirection: 'column', gap: 10, padding: '0 16px 16px' },
  mCard: { border: '1px solid var(--border)', borderRadius: 12, background: '#FDFBF8', padding: '12px 14px', minWidth: 0 },
  mName: { fontSize: 16, flexWrap: 'wrap', overflowWrap: 'anywhere' },
  mEmail: { fontSize: 14, color: 'var(--text-secondary)', overflowWrap: 'anywhere', marginTop: 2 },
  mRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '6px 0', marginTop: 6, borderTop: '1px solid #f3efe9', minHeight: 32 },
  mLabel: { fontSize: 12, fontWeight: 600, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 },
  mValue: { fontSize: 15, color: 'var(--text-secondary)', textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' },
  mControl: { ...tap, fontSize: 15, justifyContent: 'center' },
  mFullBtn: { ...tap, width: '100%', justifyContent: 'center', marginTop: 10, fontSize: 14 },
  mHint: { fontSize: 13, color: 'var(--text-light)', marginTop: 6, textAlign: 'center' },
};
